import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate,
         useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import SetPassword from "@/pages/SetPassword";
import Dashboard from "@/pages/Dashboard";
import Trainings from "@/pages/Trainings";
import TrainingForm from "@/pages/TrainingForm";
import TrainingDetail from "@/pages/TrainingDetail";
import Employees from "@/pages/Employees";
import EmployeeHistory from "@/pages/EmployeeHistory";
import Masters from "@/pages/Masters";
import Certificates from "@/pages/Certificates";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import Verify from "@/pages/Verify";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import CalendarPage from "@/pages/CalendarPage";

const HR_ROLES = ["Admin", "HR Manager", "Department Head", "Unit Coordinator", "HR", "Viewer"];
export const isHR = (u) => u && HR_ROLES.includes(u.role);

// Shown when the server could not be reached at all. Bouncing to /login instead was
// misleading — the session is fine, and the login page cannot reach the server either,
// so the user was left staring at a form that could not work.
function ServerUnreachable({ onRetry }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="server-unreachable">
      <div className="max-w-md text-center space-y-3">
        <h1 className="font-heading font-bold text-2xl">Can't reach the server</h1>
        <p className="text-sm text-muted-foreground">
          You are still signed in — Abhyas is not responding. It may be restarting.
          Your session has not ended.
        </p>
        <button onClick={onRetry} data-testid="retry-connection"
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium">
          Try again
        </button>
      </div>
    </div>
  );
}

function NotFound() {
  const nav = useNavigate();
  return (
    <div className="py-20 text-center" data-testid="not-found">
      <div className="overline text-primary">404</div>
      <h2 className="font-heading font-bold text-3xl mt-1">There is nothing at this address</h2>
      <p className="text-sm text-muted-foreground mt-2">
        The page may have been removed, or the link may be out of date.
      </p>
      <Button className="mt-6" onClick={() => nav("/")}>Back to the dashboard</Button>
    </div>
  );
}

function Protected({ children, hrOnly = true }) {
  const { user, loading, unreachable, retry } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (unreachable) return <ServerUnreachable onRetry={retry} />;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.role === "Employee") return <Navigate to="/me" replace />;
  // The API refuses everything but /auth/me, /auth/change-password and /auth/logout for
  // an account that has not set its own password, so rendering the app behind this would
  // draw a shell whose every panel answers 403.
  if (user.must_change_password) return <SetPassword />;
  return <AppShell>{children}</AppShell>;
}

function EmployeeRoute() {
  const { user, loading, unreachable, retry } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (unreachable) return <ServerUnreachable onRetry={retry} />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "Employee") return <Navigate to="/" replace />;
  return <EmployeeDashboard />;
}

// A DATA router, not <BrowserRouter>. `useBlocker` — the only way to hold a navigation
// that React Router itself performs — works exclusively with this one, and without it
// the attendance screen could hold a tab switch and a tab close but not the sidebar:
// clicking "Trainings" with a hundred marks entered threw them away in silence, which is
// the likeliest way to lose them and the only exit that was not guarded.
//
// AuthProvider sits OUTSIDE it. It reads no router hook (AppShell is what calls
// useNavigate, and that renders inside a route), so it does not need to be in the tree.
const router = createBrowserRouter(createRoutesFromElements(
  <>
    <Route path="/login" element={<Login />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/verify/:certNumber" element={<Verify />} />
    <Route path="/me" element={<EmployeeRoute />} />
    <Route path="/" element={<Protected><Dashboard /></Protected>} />
    <Route path="/trainings" element={<Protected><Trainings /></Protected>} />
    <Route path="/trainings/new" element={<Protected><TrainingForm /></Protected>} />
    <Route path="/trainings/:id" element={<Protected><TrainingDetail /></Protected>} />
    <Route path="/trainings/:id/edit" element={<Protected><TrainingForm /></Protected>} />
    <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
    <Route path="/certificates" element={<Protected><Certificates /></Protected>} />
    <Route path="/employees" element={<Protected><Employees /></Protected>} />
    <Route path="/employees/:id" element={<Protected><EmployeeHistory /></Protected>} />
    <Route path="/masters" element={<Protected><Masters /></Protected>} />
    <Route path="/reports" element={<Protected><Reports /></Protected>} />
    <Route path="/users" element={<Protected><Users /></Protected>} />
    <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
    <Route path="/settings" element={<Protected><Settings /></Protected>} />
    {/* Anything else. Without this an unknown address rendered nothing at all — a
        white page — and /approvals became exactly that when the screen was removed,
        for anyone who had it bookmarked. */}
    <Route path="*" element={<Protected><NotFound /></Protected>} />
  </>
));

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster richColors position="bottom-right" />
        <RouterProvider router={router} />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
